/**
 * 공지 읽기 상태의 주인 — 목록·마지막 읽은 번호·안 읽은 수.
 *
 * 배너와 패널은 **한 화면에 같이 있다**(PlatformHomePage). 각자 조회하고 각자
 * 상태를 들면 배너에서 읽음을 눌러도 옆 패널은 "안 읽음"으로 남는다. 읽음은
 * 사용자 한 사람의 상태 하나이므로 주인도 하나다 — 화면은 여기만 본다.
 *
 * **읽음을 올리는 자리도 여기 하나다.** 본문 화면이 서버를 직접 치면 주인이
 * 둘이 되어, 읽고 홈으로 돌아와도 배너가 캐시된 안 읽음 수를 그린다.
 *
 * **세는 쪽은 서버다.** 읽음 처리 뒤 안 읽은 수를 화면에서 다시 계산하지
 * 않고 목록을 다시 받는다. 화면이 세면 목록이 잘리거나 초안이 섞일 때 조용히
 * 틀린다 — 그 규칙이 이 파일을 지나며 흐려지면 안 된다.
 */
import { create } from 'zustand';
import { onSignOut } from '@/app/stores/signOutHooks';
import {
  listAnnouncements,
  markAnnouncementsRead,
} from '@/app/services/announcement/announcementService';
import type { AnnouncementItem } from '@/app/services/announcement/announcementService';

export interface AnnouncementState {
  items: AnnouncementItem[];
  lastReadNumber: number;
  /** 서버가 센다 */
  unreadCount: number;
  /** 한 번이라도 응답을 받았는가 — 아직이면 화면은 자리표시자를 둔다 */
  loaded: boolean;
  failed: boolean;
  /** 지금 스토어에 담긴 것이 초안까지 포함한 목록인가(admin 화면) */
  includeDrafts: boolean;

  load: (options?: { includeDrafts?: boolean }) => Promise<void>;
  markReadUpTo: (number: number) => Promise<void>;
  reset: () => void;
}

const EMPTY = {
  items: [] as AnnouncementItem[],
  lastReadNumber: 0,
  unreadCount: 0,
  loaded: false,
  failed: false,
  includeDrafts: false,
};

/** 같은 화면의 여러 칸이 동시에 뜰 때 요청이 겹치지 않게 — 진행 중인 조회 하나를 함께 기다린다 */
let inFlight: Promise<void> | null = null;
/** 진행 중인 조회가 초안까지 묻고 있는지 — 범위가 다르면 그 응답을 쓸 수 없다 */
let inFlightDrafts = false;

/**
 * 세대 — `reset()` 이 올린다. 로그아웃 직전에 떠난 조회가 뒤늦게 도착해서 앞
 * 사용자의 읽음 상태를 되살리는 창을 막는다. 응답을 쓰기 전에 세대를 대조한다.
 */
let generation = 0;

export const useAnnouncementStore = create<AnnouncementState>()((set, get) => ({
  ...EMPTY,

  load: async (options = {}) => {
    const includeDrafts = options.includeDrafts ?? false;
    // 들어올 때마다 새로 받는다 — 캐시를 그리면 그 사이 올라온 공지도, 다른
    // 화면에서 올린 읽음도 새로고침 전까지 안 보인다. 아끼는 것은 **겹치는
    // 요청**뿐이다: 같은 범위를 이미 묻고 있으면 그 응답을 함께 기다린다.
    if (inFlight && inFlightDrafts === includeDrafts) return inFlight;
    if (inFlight) await inFlight.catch(() => {});

    const bornIn = generation;
    inFlightDrafts = includeDrafts;
    inFlight = (async () => {
      try {
        const list = await listAnnouncements({ includeDrafts });
        if (bornIn !== generation) return; // 다른 사용자의 응답이다 — 버린다
        set({
          // 차례는 서버가 정한다(`ORDER BY a.number DESC NULLS FIRST` — 아직
          // 번호가 없는 초안이 맨 앞). 여기서 번호로 다시 줄 세우면 초안이
          // 맨 뒤로 밀린다. 화면은 거르기만 한다.
          items: list.announcements,
          lastReadNumber: list.lastReadNumber,
          unreadCount: list.unreadCount,
          loaded: true,
          failed: false,
          includeDrafts,
        });
      } catch {
        if (bornIn === generation) set({ loaded: true, failed: true });
      } finally {
        if (bornIn === generation) inFlight = null;
      }
    })();
    return inFlight;
  },

  markReadUpTo: async (number) => {
    await markAnnouncementsRead(number);
    // 세는 쪽은 서버 — 다시 받아서 맞춘다
    await get().load({ includeDrafts: get().includeDrafts });
  },

  reset: () => {
    generation += 1;
    inFlight = null;
    set({ ...EMPTY });
  },
}));

// 읽음은 **사용자별** 상태다 — 같은 탭의 다음 사용자가 앞 사용자의 것을 보면 안 된다
onSignOut(() => useAnnouncementStore.getState().reset());
